/**
 * Filtered Product List Web Component
 *
 * Dynamically loads and filters products via Shopify Section Rendering API
 * Replaces skeleton cards with real products based on liquid filter parameters
 */

class FilteredProductList extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.hasLoaded = false;
  }

  connectedCallback() {
    this.sectionId = this.getAttribute('section-id');
    this.collectionUrl = this.getAttribute('collection-url');
    this.parameters = new URLSearchParams();
    this.productId = this.getAttribute('product-id');
    this.minimumVisible = Number(this.getAttribute('minimum-visible')) || 0;
    this.maximumVisible = Number(this.getAttribute('maximum-visible')) || 20;

    if (Number.isNaN(this.minimumVisible)) {
      this.minimumVisible = 0;
    }

    // Parse filter parameters from liquid output
    const filterString = this.getAttribute('filters')?.trim() || '';
    const filterParameters = filterString.split(',').filter((param) => param.trim());

    for (const param of filterParameters) {
      const [key, value] = param.split('=');

      if (!key || !value) {
        continue;
      }

      this.parameters.append(key.trim(), value.trim());
    }

    // Add required parameters
    this.parameters.set('section_id', this.sectionId);
    this.parameters.set('filter.v.availability', '1'); // Only show available products

    // Only setup observer if we have filters to apply
    if (filterParameters.length > 0) {
      this.setupObserver();
    }
  }

  setupObserver() {
    const options = {
      root: null, // viewport
      rootMargin: '20% 0px', // trigger 20% before entering viewport
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.hasLoaded) {
          this.hasLoaded = true;
          this.loadContent();
          this.observer.disconnect();
        }
      });
    }, options);

    this.observer.observe(this);
  }

  /**
   * Helper: Find product cards in the document using multiple selector strategies
   */
  findProductCards(doc) {
    const selectors = [
      '.product-grid-item', // Primary: Palo Alto theme product cards (always present)
      '[data-product-block]', // Secondary: Attribute-based selector (always present)
      '.grid__item', // Fallback: Grid layout items (may include promo blocks)
      '.carousel__item', // Fallback: Carousel items (may include promo blocks)
    ];

    for (const selector of selectors) {
      const cards = doc.querySelectorAll(selector);
      if (cards.length > 0) {
        // Filter to ensure we only get actual product cards, not promo blocks
        const productCards = [...cards].filter((card) => {
          // Exclude promo blocks (they never have product-grid-item class)
          if (card.classList.contains('collection-promo') || card.hasAttribute('data-promo')) {
            return false;
          }
          // Must have product-grid-item class or data-product-block attribute
          return card.classList.contains('product-grid-item') || card.hasAttribute('data-product-block');
        });

        if (productCards.length > 0) {
          return productCards;
        }
      }
    }

    console.warn('No product cards found in response');
    return [];
  }

  /**
   * Helper: Extract product ID from a product card element
   * Product cards have ID format: "product-item--{section-id}-{product-id}"
   */
  getProductIdFromCard(card) {
    // Try to get product ID from the card's ID attribute
    // Format: "product-item--{section-id}-{product-id}"
    const cardId = card.id;
    if (cardId && cardId.startsWith('product-item--')) {
      const parts = cardId.split('-');
      const productId = parts[parts.length - 1];
      if (productId) {
        return productId;
      }
    }

    // Fallback: Look for product form with data-product-form
    const productForm = card.querySelector('[data-product-form]');
    if (productForm && productForm.id) {
      // Form ID format: "{section-id}-{product-id}"
      const formParts = productForm.id.split('-');
      const productId = formParts[formParts.length - 1];
      if (productId) {
        return productId;
      }
    }

    // Fallback: Look for product link
    const productLink = card.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      const match = href.match(/\/products\/([^?#]+)/);
      if (match) {
        return match[1]; // Return product handle
      }
    }

    return null;
  }

  /**
   * Helper: Preserve static content elements before DOM replacement
   * Finds all elements with data-static attribute and stores their outerHTML
   * @param {Element} container - The container to search for static elements
   * @returns {Map} Map of data-static keys to outerHTML strings
   */
  preserveStaticContent(container) {
    const staticElements = container.querySelectorAll('[data-static]');
    const preserved = new Map();

    staticElements.forEach((element) => {
      const key = element.getAttribute('data-static');
      if (key) {
        preserved.set(key, element.outerHTML);
      }
    });

    return preserved;
  }

  /**
   * Helper: Restore static content elements after DOM replacement
   * Finds all elements with data-static attribute and replaces them with preserved content
   * @param {Element} container - The container to search for static elements
   * @param {Map} preserved - Map of data-static keys to outerHTML strings
   */
  restoreStaticContent(container, preserved) {
    if (!preserved || preserved.size === 0) {
      return;
    }

    const staticElements = container.querySelectorAll('[data-static]');

    staticElements.forEach((element) => {
      const key = element.getAttribute('data-static');
      if (key && preserved.has(key)) {
        element.outerHTML = preserved.get(key);
      }
    });
  }

  /**
   * Helper: Update collection links with filter parameters
   * Updates the view all button and heading link hrefs with filter query params
   * @param {Element} container - The container to search for links
   */
  updateCollectionLinks(container) {
    // Build filter query string (without section_id)
    const params = new URLSearchParams(this.parameters);
    params.delete('section_id');
    const filterQuery = params.toString();

    if (!filterQuery) {
      return;
    }

    // Update all view all buttons (desktop + mobile)
    const viewAllButtons = container.querySelectorAll('.btn--view-all');
    viewAllButtons.forEach(button => {
      button.href = `${this.collectionUrl}?${filterQuery}`;
    });

    // Update heading link if it exists
    const headingLink = container.querySelector('.section__link');
    if (headingLink) {
      headingLink.href = `${this.collectionUrl}?${filterQuery}`;
    }
  }

  async loadContent() {
    try {
      const url = `${this.collectionUrl}?${this.parameters.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const shopifySection = this.closest('.shopify-section');

      if (!shopifySection) {
        console.error('Could not find .shopify-section container');
        return;
      }

      // Parse the response HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find product cards using dynamic selector
      const productCards = this.findProductCards(doc);

      if (productCards.length === 0) {
        console.error('No products found in Section Rendering API response');
        this.hideSection();
        return;
      }

      // Count actual products (excluding current product if on PDP)
      let validProductCount = productCards.length;

      // Remove current product from the list if on PDP
      if (this.productId) {
        const currentProductIndex = productCards.findIndex((card) => {
          const cardProductId = this.getProductIdFromCard(card);
          return cardProductId && cardProductId === this.productId;
        });

        if (currentProductIndex !== -1) {
          const productToRemove = productCards[currentProductIndex];
          // The product card itself IS the grid/carousel item
          productToRemove.remove();
          productCards.splice(currentProductIndex, 1);
          validProductCount--;
        }
      }

      // Limit to maximum visible products if needed
      if (validProductCount > this.maximumVisible) {
        const cardsToRemove = productCards.slice(this.maximumVisible);
        cardsToRemove.forEach((card) => {
          // The product card itself IS the grid/carousel item
          card.remove();
        });
        validProductCount = this.maximumVisible;
      }

      // Check if we have enough products to show
      if (validProductCount < this.minimumVisible) {
        this.hideSection();
        return;
      }

      // Find the section container (with data-section-type attribute)
      const sectionContainer = shopifySection.querySelector('[data-section-type="filtered-collection"]');
      const newSectionContainer = doc.querySelector('[data-section-type="filtered-collection"]');

      if (!sectionContainer || !newSectionContainer) {
        console.error('Could not find section containers');
        return;
      }

      // Get the section instance from Shopify's section management system
      const instance =
        window.Shopify?.theme?.sections?.instances?.find((inst) => inst.id === this.sectionId) || null;

      // Preserve static content (e.g., product-specific headings) before replacement
      const preservedContent = this.preserveStaticContent(sectionContainer);

      // Unload the old section instance (cleanup carousels, etc.)
      if (instance && typeof instance.onUnload === 'function') {
        instance.onUnload();
      }

      // Replace the content (preserves the section container)
      sectionContainer.innerHTML = newSectionContainer.innerHTML;

      // Restore static content (e.g., product-specific headings) after replacement
      this.restoreStaticContent(sectionContainer, preservedContent);

      // Update collection links with filter parameters (after static restoration)
      this.updateCollectionLinks(sectionContainer);

      // Reload the section instance (reinitialize carousels, etc.)
      if (instance && typeof instance.onLoad === 'function') {
        instance.onLoad();
      }

      // Dispatch event for other features to reinitialize (e.g., sticky product)
      this.reinitializeScripts();
    } catch (error) {
      console.error('Failed to load filtered products:', error);
      // On error, hide the section gracefully
      this.hideSection();
    }
  }

  hideSection() {
    const shopifySection = this.closest('.shopify-section');
    if (shopifySection) {
      shopifySection.style.display = 'none';
    }
  }

  reinitializeScripts() {
    // Reinitialize AOS (Animate On Scroll) if present
    if (window.AOS) {
      window.AOS.refresh();
    }

    // Dispatch custom event for other scripts to hook into
    document.dispatchEvent(
      new CustomEvent('filtered-product-list:loaded', {
        detail: { sectionId: this.sectionId },
      })
    );
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Register the custom element
if (!customElements.get('filtered-product-list')) {
  customElements.define('filtered-product-list', FilteredProductList);
}

export default FilteredProductList;
